/**
 * useClubData Hook
 * Manages fetching and caching of club data including details, programs, and categories
 * Extracted from ClubDetails.js
 */

import { useState, useEffect, useCallback } from 'react';

const backendUrl = process.env.REACT_APP_BACKEND_URL || 'http://localhost:5000';

/**
 * Default categories when API fails
 */
const DEFAULT_CATEGORIES = [
  { _id: 'all', name: 'All Programs', isDefault: true },
  { _id: 'recreational', name: 'Recreational', isDefault: true },
  { _id: 'competitive', name: 'Competitive', isDefault: true },
  { _id: 'academy', name: 'Academy', isDefault: true },
  { _id: 'leagues', name: 'Leagues', isDefault: true },
  { _id: 'camps', name: 'Camps', isDefault: true },
  { _id: 'trainings', name: 'Trainings', isDefault: true }
];

/**
 * Hook to fetch and manage club data
 * @param {string} clubId - The club ID to fetch data for
 * @returns {Object} Club data, loading states, and refetch functions
 */
const useClubData = (clubId) => {
  // Data states
  const [clubDetails, setClubDetails] = useState(null);
  const [programs, setPrograms] = useState([]);
  const [categories, setCategories] = useState([]);
  
  // Loading states
  const [clubLoading, setClubLoading] = useState(true);
  const [programsLoading, setProgramsLoading] = useState(true);
  const [categoriesLoading, setCategoriesLoading] = useState(true);
  
  // Error states
  const [clubError, setClubError] = useState(null);
  const [programsError, setProgramsError] = useState(null);
  const [categoriesError, setCategoriesError] = useState(null);

  /**
   * Fetch club details
   */
  const fetchClubDetails = useCallback(async () => {
    if (!clubId) return;
    
    try {
      setClubLoading(true);
      setClubError(null);
      
      const response = await fetch(`${backendUrl}/api/clubs/${clubId}`);
      
      if (!response.ok) {
        throw new Error('Failed to fetch club details');
      }
      
      const data = await response.json();
      setClubDetails(data);
    } catch (err) {
      console.error('Error fetching club details:', err);
      setClubError(err.message || 'Failed to load club details');
    } finally {
      setClubLoading(false);
    }
  }, [clubId]);

  /**
   * Fetch programs for the club
   */
  const fetchPrograms = useCallback(async () => {
    if (!clubId) return;
    
    try {
      setProgramsLoading(true);
      setProgramsError(null);
      
      const response = await fetch(`${backendUrl}/api/programs/club/${clubId}`);
      
      if (!response.ok) {
        // 404 means club has no programs - this is valid, not an error
        if (response.status === 404) {
          setPrograms([]);
        } else {
          setProgramsError('Failed to load programs');
        }
        return;
      }
      
      const data = await response.json();
      setPrograms(data);
    } catch (err) {
      // Network error - don't log, just set empty
      setPrograms([]);
    } finally {
      setProgramsLoading(false);
    }
  }, [clubId]);

  /**
   * Fetch categories for the club
   */
  const fetchCategories = useCallback(async () => {
    if (!clubId) return;
    
    try {
      setCategoriesLoading(true);
      setCategoriesError(null);
      
      const response = await fetch(`${backendUrl}/api/categories/club/${clubId}`);
      
      if (!response.ok) {
        throw new Error('Failed to fetch categories');
      }
      
      const data = await response.json();
      setCategories(data);
    } catch (err) {
      console.error('Error fetching categories:', err);
      setCategoriesError(err.message || 'Failed to load categories');
      // Set default categories if API fails
      setCategories(DEFAULT_CATEGORIES);
    } finally {
      setCategoriesLoading(false);
    }
  }, [clubId]);

  /**
   * Fetch a single program by ID (for refreshing after edit)
   */
  const fetchSingleProgram = useCallback(async (programId) => {
    try {
      const response = await fetch(`${backendUrl}/api/programs/${programId}`);
      
      if (!response.ok) {
        throw new Error('Failed to fetch program');
      }
      
      const programData = await response.json();
      
      // Add timestamp to force component re-render
      programData._lastUpdated = Date.now();
      
      // Update the program in the programs array
      setPrograms(prevPrograms => 
        prevPrograms.map(p => 
          p._id === programId ? programData : p
        )
      );
      
      return programData;
    } catch (err) {
      console.error('Error fetching single program:', err);
      throw err;
    }
  }, []);

  /**
   * Delete a program
   */
  const deleteProgram = useCallback(async (programId) => {
    try {
      const response = await fetch(`${backendUrl}/api/programs/${programId}`, {
        method: 'DELETE',
      });

      if (!response.ok) {
        throw new Error('Failed to delete program');
      }

      // Remove from local state
      setPrograms(prevPrograms => 
        prevPrograms.filter(p => p._id !== programId)
      );
      
      return true;
    } catch (err) {
      console.error('Error deleting program:', err);
      throw err;
    }
  }, []);

  /**
   * Initial data fetch
   */
  useEffect(() => {
    if (clubId) {
      fetchClubDetails();
      fetchCategories();
      fetchPrograms();
    }
  }, [clubId, fetchClubDetails, fetchCategories, fetchPrograms]);

  // Combined loading state
  const isLoading = clubLoading || programsLoading || categoriesLoading;
  
  // Initial loading (only true when first loading with no data)
  const isInitialLoading = clubLoading && !clubDetails;
  
  // Any error
  const hasError = !!(clubError || programsError);
  const error = clubError || programsError || categoriesError;

  return {
    // Data
    clubDetails,
    programs,
    categories,
    
    // Loading states
    isLoading,
    isInitialLoading,
    clubLoading,
    programsLoading,
    categoriesLoading,
    
    // Error states
    hasError,
    error,
    clubError,
    programsError,
    categoriesError,
    
    // Refetch functions
    refetch: {
      club: fetchClubDetails,
      programs: fetchPrograms,
      categories: fetchCategories,
      all: () => {
        fetchClubDetails();
        fetchCategories();
        fetchPrograms();
      }
    },
    
    // Mutation helpers
    fetchSingleProgram,
    deleteProgram,
    
    // Direct setters (for optimistic updates)
    setPrograms,
    setClubDetails
  };
};

export default useClubData;
